import { useEffect, useRef, useState } from 'react';
import {
  ShieldCheck,
  Plus,
  Trash2,
  LogIn,
  RefreshCw,
  X,
  Eye,
  EyeOff,
  AlertCircle,
  UserRound,
} from 'lucide-react';
import {
  apiSuperAdminListAdmins,
  apiSuperAdminCreateAdmin,
  apiSuperAdminDeleteAdmin,
  apiSuperAdminImpersonate,
} from '../api/client';

// ── helpers ────────────────────────────────────────────────────────────────

function Avatar({ email }) {
  const letter = (email ?? '?')[0].toUpperCase();
  const colors = [
    'bg-brand-500',
    'bg-green-500',
    'bg-amber-500',
    'bg-pink-500',
    'bg-brand-400',
    'bg-green-400',
  ];
  const idx = (email ?? '').charCodeAt(0) % colors.length;
  return (
    <span
      className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${colors[idx]}`}
    >
      {letter}
    </span>
  );
}

// ── Add Admin Modal ────────────────────────────────────────────────────────

function AddAdminModal({ onClose, onCreated, t }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const emailRef = useRef(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError(t('superAdminCreateValidation'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const created = await apiSuperAdminCreateAdmin({ email: email.trim(), password });
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('superAdminCreateError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
              <UserRound size={18} />
            </span>
            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              {t('superAdminAddAdmin')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
              {t('superAdminEmail')}
            </label>
            <input
              ref={emailRef}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              required
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
              {t('superAdminPassword')}
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('superAdminPasswordPlaceholder')}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 pe-10 text-sm text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                tabIndex={-1}
              >
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? (
                <RefreshCw size={15} className="animate-spin" />
              ) : (
                <Plus size={15} />
              )}
              {t('superAdminCreate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Admin Card ─────────────────────────────────────────────────────────────

function AdminCard({ admin, onDelete, onImpersonate, t }) {
  const [deleting, setDeleting] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await onDelete(admin.id);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleImpersonate = async () => {
    setImpersonating(true);
    try {
      await onImpersonate(admin.id);
    } finally {
      setImpersonating(false);
    }
  };

  return (
    <article className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-3">
        <Avatar email={admin.email} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
            {admin.email}
          </p>
          <p className="mt-0.5 truncate text-xs text-slate-400">
            {admin.userName ?? admin.email}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
          Admin
        </span>
      </div>

      <div className="flex gap-2">
        {/* Login As */}
        <button
          type="button"
          onClick={handleImpersonate}
          disabled={impersonating}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-600 px-3 py-2 text-xs font-semibold text-white shadow hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {impersonating ? (
            <RefreshCw size={13} className="animate-spin" />
          ) : (
            <LogIn size={13} />
          )}
          {t('superAdminLoginAs')}
        </button>

        {/* Delete */}
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleting}
          className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
            confirmDelete
              ? 'bg-pink-600 text-white hover:bg-pink-700'
              : 'border border-slate-200 text-slate-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600 dark:border-slate-700 dark:text-slate-400'
          }`}
          title={t('superAdminDelete')}
        >
          {deleting ? (
            <RefreshCw size={13} className="animate-spin" />
          ) : (
            <Trash2 size={13} />
          )}
          {confirmDelete ? t('superAdminConfirmDelete') : t('superAdminDelete')}
        </button>
      </div>
    </article>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

function SuperAdminPage({ t, onToast, onLoginAs }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiSuperAdminListAdmins();
      setAdmins(data);
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : t('superAdminLoadError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreated = (newAdmin) => {
    setAdmins((prev) => [...prev, newAdmin]);
    setShowAddModal(false);
    onToast?.(t('superAdminCreatedToast'), 'success');
  };

  const handleDelete = async (id) => {
    try {
      await apiSuperAdminDeleteAdmin(id);
      setAdmins((prev) => prev.filter((a) => a.id !== id));
      onToast?.(t('superAdminDeletedToast'), 'success');
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : t('superAdminDeleteError'), 'error');
    }
  };

  const handleImpersonate = async (id) => {
    try {
      const result = await apiSuperAdminImpersonate(id);
      // result: { token, expiresUtc, email, userId }
      onLoginAs?.(result);
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : t('superAdminImpersonateError'), 'error');
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Hero */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#1A1A1A] via-brand-600 to-green-500 p-6 text-white shadow-xl shadow-brand/20 dark:border-slate-800">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold">
              <ShieldCheck size={16} />
              {t('superAdminBadge')}
            </div>
            <h1 className="text-3xl font-bold md:text-4xl">{t('superAdminTitle')}</h1>
            <p className="mt-3 text-sm leading-6 text-brand-50 md:text-base">
              {t('superAdminSubtitle')}
            </p>
          </div>
          <div className="flex shrink-0 gap-3">
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur hover:bg-white/20 disabled:opacity-60"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              {t('refresh')}
            </button>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-brand-700 shadow hover:bg-brand-50"
            >
              <Plus size={15} />
              {t('superAdminAddAdmin')}
            </button>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          {
            label: t('superAdminTotalAdmins'),
            value: loading ? '…' : admins.length,
            color: 'text-brand-600 dark:text-brand-400',
          },
          {
            label: t('superAdminRoleLabel'),
            value: 'SuperAdmin',
            color: 'text-emerald-600 dark:text-emerald-400',
          },
          {
            label: t('superAdminScopeLabel'),
            value: t('superAdminScopeValue'),
            color: 'text-amber-600 dark:text-amber-400',
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              {stat.label}
            </p>
            <p className={`mt-1 text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-36 animate-pulse rounded-2xl border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-800"
            />
          ))}
        </div>
      ) : admins.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-slate-300 py-16 dark:border-slate-700">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800">
            <ShieldCheck size={26} />
          </span>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('superAdminEmpty')}</p>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus size={15} />
            {t('superAdminAddAdmin')}
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {admins.map((admin) => (
            <AdminCard
              key={admin.id}
              admin={admin}
              onDelete={handleDelete}
              onImpersonate={handleImpersonate}
              t={t}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddAdminModal
          onClose={() => setShowAddModal(false)}
          onCreated={handleCreated}
          t={t}
        />
      )}
    </div>
  );
}

export default SuperAdminPage;
